import React, { useState } from "react";
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
} from "@material-ui/core";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import LaunchIcon from "@material-ui/icons/Launch";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid #C5C5C5`,
      borderRadius: 4,
      backgroundColor: "#F9F9F9",
      padding: theme.spacing(3),
      marginTop: theme.spacing(4),
      [theme.breakpoints.down("sm")]: {
        border: 0,
        paddingLeft: 0,
      },
    },
    title: {
      fontWeight: "bold",
      color: "#37474F",
      fontSize: 18,
      marginBottom: theme.spacing(2),
    },
    buttonContainer: {
      marginTop: theme.spacing(2),
    },
    button: {
      margin: theme.spacing(1, 0),
      justifyContent: "flex-start",
      textTransform: "none",
      backgroundColor: "#FFFFFF",
      color: "#37474F",
      borderColor: "#B0BEC5",
      borderWidth: 1,
      borderStyle: "solid",
      "&:hover": {
        backgroundColor: "#ECEFF1",
      },
      "& svg": {
        marginRight: theme.spacing(1),
      },
    },
    linkText: {
      fontSize: 16,
      fontWeight: 500,
    },
    fallbackContainer: {
      textAlign: "center",
      padding: theme.spacing(4),
    },
    fallbackMessage: {
      fontSize: 18,
      color: "#37474F",
    },
    linkCard: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      border: `1px solid #E0E0E0`,
      backgroundColor: "#FFFFFF",
      transition: "box-shadow 0.2s ease-in-out",
      cursor: "pointer",
      "&:hover": {
        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
      },
    },
    iconWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 48,
      height: 48,
      borderRadius: "50%",
      backgroundColor: "#ECEFF1",
      marginRight: theme.spacing(2),
    },
    linkTextContainer: {
      flexGrow: 1,
    },
    linkLabel: {
      fontSize: 16,
      fontWeight: 500,
      color: "#37474F",
    },
    iconButton: {
      marginLeft: theme.spacing(1),
    },
    infoText: {
      color: "#37474F",
      fontSize: 16,
      marginBottom: theme.spacing(2),
      lineHeight: 1.5,
    },
    iframeContainer: {
      width: "100%",
      height: "800px",
      border: "none",
      marginTop: theme.spacing(4),
    },
  })
);

type LinkItem = {
  label: string;
  url: string;
};

const CiscoMeetingSpacesBrisbane = () => {
  const classes = useStyles();
  const theme = useTheme();
  const mdAndUp = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const links: LinkItem[] = [
    {
      label: "Level 9 ",
      url: "https://workspaces.ciscospaces.io/?token=196d6838-657e-4f4f-aab9-b0f3ddea24e3",
    },
    {
      label: "Level 10 ",
      url: "https://workspaces.ciscospaces.io/?token=4042d89c-aee7-48a7-a78c-0caac549c02f",
    },
    {
      label: "Level 11 ",
      url: "https://workspaces.ciscospaces.io/?token=97421d56-297e-434b-97d7-e91cf27c088b",
    },
    {
      label: "Level 12 ",
      url: "https://workspaces.ciscospaces.io/?token=47b20c30-2a33-4aff-839d-2e16509ab88d",
    },
    {
      label: "Level 13 ",
      url: "https://workspaces.ciscospaces.io/?token=f8486668-d60c-448b-8758-0eba566756f2",
    },
    {
      label: "Level 14 ",
      url: "https://workspaces.ciscospaces.io/?token=f52f7fa7-e2c1-4c18-b612-d2487c11254d",
    },
    {
      label: "Level 15 ",
      url: "https://workspaces.ciscospaces.io/?token=804444b7-defd-4a62-bbf1-8701e0e19c60",
    },
  ];

  const handleLinkClick = (url: string) => {
    setSelectedUrl(url);
  };

  return (
    <Box className={classes.container}>
      {selectedUrl ? (
        <Box>
          {/* <Typography variant="h6">Building Kiosk</Typography> */}
          <iframe src={selectedUrl} className={classes.iframeContainer} title="Kiosk" />
          <Box mt={2}>
            <IconButton onClick={() => setSelectedUrl(null)}>
              <LaunchIcon style={{ color: "#455A64" }} />
            </IconButton>
            <Typography>Click here to go back to the list of buildings</Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Typography className={classes.infoText}>
            {" "}
            The Cisco Spaces Kiosks allow you to locate meeting spaces on the floor and hold an
            available meeting space for a short period of time, allowing you to walk in and occupy
            the space.{" "}
          </Typography>{" "}
          <Typography className={classes.infoText} style={{ fontStyle: "italic" }}>
            {" "}
            Cisco Spaces is in the progress of being rolled out to all BHP corporate office
            locations. Where your location is not available below, please await communications from
            your local Property and Workplace team regarding the Cisco Spaces rollout in your
            location.{" "}
          </Typography>{" "}
          <Typography className={classes.infoText}>
            {" "}
            Please click one of the links below to access the buildings kiosk page.{" "}
          </Typography>
          <Grid container spacing={2}>
            {links.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  className={classes.linkCard}
                  onClick={() => handleLinkClick(item.url)}
                  elevation={0}
                >
                  <div className={classes.iconWrapper}>
                    <LocationCityIcon style={{ color: "#455A64" }} />
                  </div>
                  <div className={classes.linkTextContainer}>
                    <Typography className={classes.linkLabel}>{item.label}</Typography>
                  </div>
                  <IconButton
                    className={classes.iconButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLinkClick(item.url);
                    }}
                  >
                    <LaunchIcon style={{ color: "#455A64" }} />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default CiscoMeetingSpacesBrisbane;
